/**
 * useSocket - Custom React hook for WebSocket communication
 * Handles connection, events, session management, and reconnection
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import type {
  Message,
  ConnectionStatus,
  LeadStatus,
  ConfigResponse,
  ReconnectionMessage,
  BotMessage,
  BotTyping,
  LeadCaptured,
  SocketError,
  SessionEnded,
  UrlItem,
} from '../types/socket';

// @ts-ignore
const SOCKET_URL = import.meta.env.VITE_API_URL || window.location.origin;
const SESSION_KEY = 'ezlearn_session_id';

// Generate UUID v4
function generateSessionId(): string {
  if (window.crypto && window.crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// Get or create session ID. Returns { sessionId, isNew } so callers know
// whether the ID was freshly generated (no prior localStorage entry).
function getOrCreateSessionId(): { sessionId: string; isNew: boolean } {
  let sessionId = localStorage.getItem(SESSION_KEY);

  // Check if it's the OLD format (starts with "session_")
  if (sessionId && sessionId.startsWith('session_')) {
    console.log('Found old session format, clearing...');
    localStorage.removeItem(SESSION_KEY);
    sessionId = null;
  }

  if (!sessionId) {
    sessionId = generateSessionId();
    localStorage.setItem(SESSION_KEY, sessionId);
    return { sessionId, isNew: true };
  }
  return { sessionId, isNew: false };
}

// Convert backend message format to frontend Message
function toMessage(
  type: 'userMessage' | 'botMessage',
  content: string,
  timestamp: string,
  urls?: UrlItem[],
  isStreaming?: boolean,
  showLeadForm?: boolean
): Message {
  return {
    id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    sender: type === 'userMessage' ? 'user' : 'assistant',
    text: content,
    timestamp: new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    urls,
    isStreaming,
    showLeadForm,
  };
}

export interface UseSocketReturn {
  messages: Message[];
  isTyping: boolean;
  connectionStatus: ConnectionStatus;
  leadStatus: LeadStatus;
  sessionId: string;
  sendMessage: (text: string) => void;
  submitLead: (data: { name: string; email: string; phone: string }) => void;
  endChat: () => void;
  resetSession: () => void;
  startSession: (sid?: string) => void;
}

export function useSocket(): UseSocketReturn {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('connecting');
  const [leadStatus, setLeadStatus] = useState<LeadStatus>({ name: false, email: false, phone: false });

  // True when the session ID was just created on this page load (no prior localStorage entry).
  const isNewSessionRef = useRef(true);
  const [sessionId, setSessionId] = useState<string>(() => {
    const { sessionId, isNew } = getOrCreateSessionId();
    isNewSessionRef.current = isNew;
    return sessionId;
  });

  const socketRef = useRef<Socket | null>(null);
  // Tracks whether config/reconnect was already emitted for the current session ID.
  // Prevents double-greeting when connect handler and handleOpen both fire on first open.
  const sessionInitializedRef = useRef(false);

  useEffect(() => {
    const socket = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
    });

    socketRef.current = socket;

    // Connection watchdog: if socket stays in 'connecting' state for >10s,
    // reset to a fresh session to avoid blank chat from stalled connections.
    const watchdogTimer = setTimeout(() => {
      if (!socket.connected) {
        console.warn('Connection watchdog: socket stalled for 10s, resetting session');
        socket.disconnect();
        localStorage.removeItem(SESSION_KEY);
        const freshId = generateSessionId();
        localStorage.setItem(SESSION_KEY, freshId);
        setSessionId(freshId);
        sessionInitializedRef.current = false;
        const freshSocket = io(SOCKET_URL, {
          transports: ['websocket', 'polling'],
          reconnection: true,
          reconnectionAttempts: Infinity,
          reconnectionDelay: 1000,
          reconnectionDelayMax: 10000,
        });
        socketRef.current = freshSocket;
        freshSocket.on('connect', () => {
          console.log('Watchdog: fresh socket connected');
          setConnectionStatus('online');
          freshSocket.emit('config', { session_id: freshId });
          sessionInitializedRef.current = true;
        });
      }
    }, 10000);

    // Connection established
    socket.on('connect', () => {
      console.log('Socket connected');
      clearTimeout(watchdogTimer);
      setConnectionStatus('online');

      const existingSessionId = localStorage.getItem(SESSION_KEY);

      // Check if it's OLD format - clear it
      if (existingSessionId && existingSessionId.startsWith('session_')) {
        console.log('Found old session format, clearing and creating new session...');
        localStorage.removeItem(SESSION_KEY);
        const newSessionId = generateSessionId();
        localStorage.setItem(SESSION_KEY, newSessionId);
        setSessionId(newSessionId);
        console.log('New session created:', newSessionId.substring(0, 8) + '...');
        socket.emit('config', { session_id: newSessionId });
      } else if (existingSessionId && !isNewSessionRef.current) {
        // Reconnect with a session that existed before this page load
        console.log('Reconnecting with session:', existingSessionId.substring(0, 8) + '...');
        setSessionId(existingSessionId);
        socket.emit('reconnect', { session_id: existingSessionId });
      } else {
        // New session created on this page load — use config, not reconnect
        const sid = existingSessionId || generateSessionId();
        if (!existingSessionId) localStorage.setItem(SESSION_KEY, sid);
        setSessionId(sid);
        console.log('New session:', sid.substring(0, 8) + '...');
        socket.emit('config', { session_id: sid });
        isNewSessionRef.current = false; // future reconnects should use 'reconnect'
      }
      sessionInitializedRef.current = true;
    });

    socket.on('disconnect', () => {
      console.log('Socket disconnected');
      setConnectionStatus('offline');
      // Clear typing indicator so chat doesn't get stuck
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      setIsTyping(false);
    });

    socket.on('reconnecting', () => {
      setConnectionStatus('reconnecting');
    });

    socket.on('connect_error', (error) => {
      console.error('Connection error:', error);
      setConnectionStatus('offline');
    });

    // New protocol events
    socket.on('configResponse', (data: ConfigResponse) => {
      console.log('Config response received');
      // Welcome message will come via botMessage event
    });

    socket.on('reconnectionMessage', (data: ReconnectionMessage) => {
      console.log('Reconnection message received', { wasExpired: data.wasExpired, historyLength: data.chat_history.length });

      if (data.chat_history.length > 0) {
        // Restore chat history. For expired/new sessions the greeting is already
        // in chat_history, so we restore it here instead of relying on a separate
        // botMessage event that can be lost in a race condition.
        const restoredMessages = data.chat_history.map((item, index) =>
          toMessage(
            item.type,
            item.content,
            item.timestamp,
            undefined,
            // Animate only the last message of a fresh/expired session
            data.wasExpired && index === data.chat_history.length - 1
          )
        );
        setMessages(restoredMessages);
      } else {
        setMessages([]);
      }
    });

    socket.on('botMessage', (data: BotMessage) => {
      console.log('Bot message received');
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      setIsTyping(false);
      const msg = toMessage('botMessage', data.content, data.timestamp, data.urls, true);
      setMessages((prev) => [...prev, msg]);
    });

    socket.on('botTyping', (data: BotTyping) => {
      setIsTyping(data.status);
      // Auto-clear typing indicator after 30s to prevent getting stuck
      if (data.status) {
        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = setTimeout(() => setIsTyping(false), 30000);
      } else {
        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      }
    });

    socket.on('leadCaptured', (data: LeadCaptured) => {
      console.log('Lead captured:', data.fields);
      setLeadStatus(data.lead_status);
    });

    socket.on('error', (data: SocketError) => {
      console.error(`Socket error [${data.code}]:`, data.message);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      setIsTyping(false);

      if (data.code === 'SESSION_EXPIRED') {
        localStorage.removeItem(SESSION_KEY);
        setMessages((prev) => [
          ...prev,
          {
            id: 'error-expired',
            sender: 'assistant',
            text: '⚠️ Your session has expired. Please refresh the page.',
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          },
        ]);
      } else {
        setMessages((prev) => [
          ...prev,
          {
            id: `error-${Date.now()}`,
            sender: 'assistant',
            text: `⚠️ ${data.message}`,
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          },
        ]);
      }
    });

    socket.on('sessionEnded', (data: SessionEnded) => {
      console.log('Session ended - creating new session');
      
      // Clear old session
      localStorage.removeItem(SESSION_KEY);
      
      // Create new session
      const newSessionId = generateSessionId();
      localStorage.setItem(SESSION_KEY, newSessionId);
      setSessionId(newSessionId);
      
      // Clear messages silently
      setMessages([]);
      
      // Initialize new session with backend
      console.log('New session created:', newSessionId.substring(0, 8) + '...');
      socket.emit('config', { session_id: newSessionId });
    });

    // Cleanup on unmount
    return () => {
      clearTimeout(watchdogTimer);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      socket.disconnect();
    };
  }, []);

  const sendMessage = useCallback((text: string) => {
    if (!text.trim() || !socketRef.current) return;
    if (!socketRef.current.connected) return; // Don't send when offline

    const timestamp = new Date().toISOString();

    // Add user message to state immediately
    const userMsg: Message = {
      id: `user-${Date.now()}`,
      sender: 'user',
      text,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };
    setMessages((prev) => [...prev, userMsg]);

    // Emit to server (new protocol)
    socketRef.current.emit('userMessage', {
      type: 'userMessage',
      content: text,
      session_id: sessionId,
      timestamp,
    });
  }, [sessionId]);

  const endChat = useCallback(() => {
    if (!socketRef.current) return;
    socketRef.current.emit('endSession', { session_id: sessionId });
  }, [sessionId]);

  const resetSession = useCallback(() => {
    // Clear old session and rotate ID — greeting will be requested on next open
    localStorage.removeItem(SESSION_KEY);
    const newSessionId = generateSessionId();
    localStorage.setItem(SESSION_KEY, newSessionId);
    setSessionId(newSessionId);
    setMessages([]);
    setIsTyping(false);
    sessionInitializedRef.current = false; // next open must emit config
    isNewSessionRef.current = true; // treat as new session
  }, []);

  const startSession = useCallback((sid?: string) => {
    if (sessionInitializedRef.current) return; // connect handler already emitted for this session
    const id = sid ?? localStorage.getItem(SESSION_KEY) ?? sessionId;
    if (socketRef.current?.connected) {
      socketRef.current.emit('config', { session_id: id });
      sessionInitializedRef.current = true;
    }
  }, [sessionId]);

  const submitLead = useCallback((data: { name: string; email: string; phone: string }) => {
    if (!socketRef.current) return;
    socketRef.current.emit('leadCapture', {
      name: data.name,
      email: data.email,
      phone: data.phone,
      session_id: sessionId,
    });
    // Remove the lead form from messages after submission
    setMessages((prev) =>
      prev.map((msg) => (msg.showLeadForm ? { ...msg, showLeadForm: false } : msg))
    );
  }, [sessionId]);

  return {
    messages,
    isTyping,
    connectionStatus,
    leadStatus,
    sessionId,
    sendMessage,
    submitLead,
    endChat,
    resetSession,
    startSession,
  };
}
